import { Stack, Text } from "@certn/cascadia";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useQueryParams } from "../../../hooks/useQueryParams";
import { useSummaryTileConfigs } from "@certn/monitoring";
import { formatMonitoringDate } from "../monitoringFormat";
import { useDashboardSummary } from "../useMonitoringApi";

export function MonitoringSummaryTiles() {
  const { t } = useTranslation();
  const { params, setParam, clearParam } = useQueryParams();
  const tiles = useSummaryTileConfigs();
  const { data } = useDashboardSummary();

  const countsByResult: Record<string, number> = {};
  (data?.results ?? []).forEach((row) => {
    countsByResult[row.result] = row.count;
  });

  const selectedResults = new Set(params.overall_result?.split(",") ?? []);

  function handleTilePress(result: string) {
    clearParam("page");
    if (selectedResults.has(result) && selectedResults.size === 1) {
      clearParam("overall_result");
      return;
    }
    setParam("overall_result", [result]);
  }

  return (
    <div className={styles.container}>
      <Stack gap="space-0">
        <Text style="global/title-small" color="text-base">
          {t("Overall result")}
        </Text>
        <Text
          style="global/body-primary"
          color="text-weak"
          testID="monitoring-summary-as-of"
        >
          {t("{{count}} applicants actively monitored as of {{date}}", {
            count: data?.actively_monitored_count ?? 0,
            date: formatMonitoringDate(data?.as_of),
          })}
        </Text>
      </Stack>
      <div className={styles.tileRow}>
        {tiles.map((tile) => {
          const isSelected = selectedResults.has(tile.result);
          return (
            <button
              key={tile.result}
              type="button"
              onClick={() => handleTilePress(tile.result)}
              aria-pressed={isSelected}
              className={css(styles.tile, {
                background: tile.background,
                outline: isSelected
                  ? `2px solid ${tile.accent}`
                  : "2px solid transparent",
              })}
              data-testid={`monitoring-summary-tile-${tile.result.toLowerCase().replace(/_/g, "-")}`}
            >
              <Text style="global/metric-xlarge" color="text-base">
                {countsByResult[tile.result] ?? 0}
              </Text>
              <Text style="global/metric-label" color={tile.labelColor}>
                {tile.label}
              </Text>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-6)",
    padding: "var(--space-6)",
    borderRadius: "var(--border-radius-4)",
    border: "1px solid var(--border-base)",
  }),
  tileRow: css({
    display: "flex",
    flexWrap: "wrap",
    gap: "var(--space-6)",
  }),
  tile: css({
    flex: "1 1 200px",
    minWidth: "200px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--space-3)",
    padding: "var(--space-6)",
    borderRadius: "var(--border-radius-3)",
    border: "none",
    cursor: "pointer",
    textAlign: "center",
  }),
};
